import { ChatBubbleBottomCenterIcon, HeartIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { format, parseISO } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";

import { Badge, Label, Small } from "@fronterahealth/frontera-ui-components";

import { type GetCommunityPostsOutput } from "@api/rest/types-and-hooks";
import { useBasecampCommunityPostsList } from "@api/rest/types-and-hooks";

interface PostSearchDropdownProps {
  placeholder: string;
  excludePostIds: number[];
  onSelect: (post: GetCommunityPostsOutput) => void;
}

export const PostSearchDropdown: React.FC<PostSearchDropdownProps> = ({ placeholder, excludePostIds, onSelect }) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, 300);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isSuccess } = useBasecampCommunityPostsList(
    { query: debouncedQuery },
    { query: { enabled: !!debouncedQuery, staleTime: 30_000 } },
  );

  const results = data?.status === 200 ? data.data : [];
  const filtered = results.filter((p) => !excludePostIds.includes(p.id));
  const showDropdown = open && !!debouncedQuery && isSuccess;
  const isEmpty = showDropdown && filtered.length === 0;

  // mousedown (not click) so this fires before the item's onMouseDown handler,
  // allowing selection to complete before the dropdown closes.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (post: GetCommunityPostsOutput) => {
    onSelect(post);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-x-2 px-3 py-2 rounded-lg border border-border-medium bg-bg-surface focus-within:border-border-focus">
        <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-icon-tertiary" />
        <input
          className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary outline-none border-none ring-0 focus:ring-0 p-0"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => query.length > 0 && setOpen(true)}
        />
      </div>

      {isEmpty && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-border-medium bg-bg-surface shadow-lg px-4 py-3">
          <Small colorType="secondary">No posts found</Small>
        </div>
      )}

      {showDropdown && filtered.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full rounded-lg border border-border-medium bg-bg-surface shadow-lg overflow-y-auto max-h-[300px] [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          {filtered.map((post) => (
            <li
              key={post.id}
              className="flex flex-col gap-y-0.5 px-4 py-3 cursor-pointer hover:bg-bg-hovered border-b border-border-subtle last:border-b-0"
              onMouseDown={() => handleSelect(post)}
            >
              <div className="flex items-center gap-x-2">
                <Label size="small" displayType="loud" colorType="primary" className="flex-1 truncate">
                  {post.name}
                </Label>
                <Badge text={post.space_name} appearance="neutral" />
              </div>
              <div className="flex items-center gap-x-2 text-text-secondary">
                <Small colorType="secondary">
                  {post.user_name}
                  {post.published_at && <> · {format(parseISO(post.published_at), "MMM d, yyyy")}</>}
                </Small>
                <span className="flex items-center gap-x-1">
                  <ChatBubbleBottomCenterIcon className="h-3.5 w-3.5" />
                  <Small colorType="secondary">{post.comment_count}</Small>
                </span>
                <span className="flex items-center gap-x-1">
                  <HeartIcon className="h-3.5 w-3.5" />
                  <Small colorType="secondary">{post.like_count}</Small>
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
